import type {
  EffectiveTranslationModel,
  EntranceEffectiveCapabilities,
  TranslationCapabilities,
  TranslationLanguage
} from './types';

export type TranslationDocsExamples = {
  curl: string;
  python: string;
  javascript: string;
};

export type TranslationExampleOptions = {
  chatCompletionsURL: string;
  model: string;
  sourceLanguage: string;
  targetLanguage: string;
  requiresKey: boolean;
  appBossBillingEnabled: boolean;
};

export function hasCapabilityProfile(view: EntranceEffectiveCapabilities | undefined, task: string) {
  return Boolean(view?.profiles.some((profile) => profile.task === task && profile.available));
}

export function effectiveTranslationModels(view: EntranceEffectiveCapabilities | undefined): EffectiveTranslationModel[] {
  if (!hasCapabilityProfile(view, 'translation')) return [];
  return (view?.models || []).filter((model) => model.capabilities?.task === 'translation');
}

export function translationCapabilityURLs(modelsPath: string, models: EffectiveTranslationModel[]): string[] {
  const root = modelsPath.replace(/\/+$/, '');
  return Array.from(new Set(
    models
      .map((model) => model.id)
      .filter((modelID) => modelID.length > 0)
      .map((modelID) => `${root}/${encodeURIComponent(modelID)}/capabilities`)
  ));
}

export function modelSourceLanguages(model: EffectiveTranslationModel | undefined): TranslationLanguage[] {
  const capabilities = model?.capabilities;
  const targets = capabilities?.effective_directions?.targets_by_source || {};
  const names = languageNames(capabilities);
  return Object.keys(targets)
    .filter((source) => targets[source]?.length > 0)
    .sort(compareLanguageCodes)
    .map((code) => ({ code, name: code === 'auto' ? 'Auto Detect' : names.get(code) || code }));
}

export function modelTargetLanguages(model: EffectiveTranslationModel | undefined, sourceLanguage: string): TranslationLanguage[] {
  const capabilities = model?.capabilities;
  const names = languageNames(capabilities);
  const targets = capabilities?.effective_directions?.targets_by_source?.[sourceLanguage] || [];
  return uniqueCodes(targets).map((code) => ({ code, name: names.get(code) || code }));
}

export function buildTranslationExamples(options: TranslationExampleOptions): TranslationDocsExamples {
  const body = {
    model: options.model,
    prompt: '你好，欢迎使用智能算力网关。',
    translation_options: {
      source_lang: options.sourceLanguage,
      target_lang: options.targetLanguage
    },
    stream: false
  };
  const prettyBody = JSON.stringify(body, null, 2);
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (options.requiresKey) headers.Authorization = 'Bearer <client-api-key>';
  if (options.appBossBillingEnabled) headers['X-APPBOSS-UID'] = '<appboss-uid>';

  const curlHeaders = Object.entries(headers).map(([name, value]) => `  -H "${name}: ${value}" \\`).join('\n');
  const pythonHeaders = JSON.stringify(headers, null, 4);
  const javascriptHeaders = JSON.stringify(headers, null, 4);
  return {
    curl: `curl -X POST "${options.chatCompletionsURL}" \\\n${curlHeaders}\n  -d '${prettyBody}'`,
    python: `import requests

response = requests.post(
    "${options.chatCompletionsURL}",
    headers=${pythonHeaders},
    json=${JSON.stringify(body, null, 4)},
    timeout=60,
)
response.raise_for_status()
print(response.json())`,
    javascript: `const response = await fetch("${options.chatCompletionsURL}", {
  method: "POST",
  headers: ${javascriptHeaders},
  body: JSON.stringify(${prettyBody})
});

if (!response.ok) throw new Error(await response.text());
console.log(await response.json());`
  };
}

export function isDirectionSelectionAvailable(model: EffectiveTranslationModel | undefined) {
  return modelSourceLanguages(model).some((source) => modelTargetLanguages(model, source.code).length > 0);
}

function languageNames(capabilities: TranslationCapabilities | undefined) {
  return new Map((capabilities?.languages || []).map((language) => [language.code, language.name]));
}

function uniqueCodes(codes: string[]) {
  return Array.from(new Set(codes.filter(Boolean)));
}

function compareLanguageCodes(left: string, right: string) {
  if (left === 'auto') return -1;
  if (right === 'auto') return 1;
  return left.localeCompare(right);
}
